import { h } from 'preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import * as COLOR_LANG from '../../../../../../lang/color.json';
import { DEFAULT_SUBTITLE_SETTINGS } from '../../../../../../settings/types';
import { USER_SUBTITLE_SETTINGS_SAVE } from '../../../../../../types';
import { SimpleTooltip } from '../../../../../common/components/tooltips/SimpleTooltip';
import Scrollable from '../../../../../common/hoc/scrollable';
import { usePlayerContext, useStyles } from '../../../../../common/hooks';
import { TEXT_LEVEL_1_FONT_SIZE_ONLY, textStyleOnXLines } from '../../../../../common/styles';
import { FONT_FAMILY_BROWN, FONT_FAMILY_OPEN_DYSLEXIC } from '../../../../../fonts';
import { connect, withBreakPoints } from '../../../../hoc';
import { COLOR_BLACK, COLOR_WHITE, COLOR_YELLOW } from '../../../../theme/colors';
import { LabeledButton, ToggleButton } from '../../../common';
import Selectable from '../../../common/menu/Selectable';
import Seperator from '../../../common/menu/Seperator';
import {
  FONT_RATIOS,
  FONT_SIZE_SUBTITLE,
  MOCK_SUBTITLE_WRAPPER,
  SECTION_CONTENT_TRACK_SETTINGS_STYLES,
  STYLES_SECTION_TRACK_SETTIONS_FOOTER,
  STYLES_SUBTITLE_SETTINGS_SECTION_CONTENT,
  STYLES_TRACK_SETTINGS_LETTER_RATIO,
  STYLES_WITH_SPACE,
  TRACK_SETTINGS_WRAPPER_STYLES
} from './styles';

const ALLOWED_SUBTITLE_COLORS = [COLOR_WHITE, COLOR_BLACK, COLOR_YELLOW];

function TextTrackSettings({
  enableAccessOutline = true,
  fontFamily: Sfont,
  color: Scolor,
  background,
  fontRatio,
  extraSmall,
  onSave
}) {
  const player = usePlayerContext();
  const [fontFamily, setUseStandardFont] = useState(Sfont || FONT_FAMILY_BROWN);
  const [enableBackground, setShowBackground] = useState(background);
  const [color, setFontColor] = useState(Scolor);
  const [autoColor, setAutoColor] = useState(!Scolor);
  const [ratio, setRatio] = useState(fontRatio);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const { fontSize } = useStyles()(FONT_SIZE_SUBTITLE);

  const subtitleStyle = useStyles()(MOCK_SUBTITLE_WRAPPER(color, fontSize), [color, fontSize]);
  const mockTrackStyle = useMemo(
    () => ({
      background: enableBackground ? COLOR_BLACK : 'none',
      padding: '8px',
      fontFamily,
      color: autoColor ? DEFAULT_SUBTITLE_SETTINGS.color : color,
      lineHeight: 1.5,
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      fontSize: `${1 * ratio}em`,
      ...textStyleOnXLines(1)
    }),
    [enableBackground, autoColor, color, ratio, fontFamily]
  );

  const resetTextTrackSettings = () => {
    setUseStandardFont(DEFAULT_SUBTITLE_SETTINGS.fontFamily);
    setShowBackground(DEFAULT_SUBTITLE_SETTINGS.background);
    setFontColor(DEFAULT_SUBTITLE_SETTINGS.color);
    setAutoColor(true);
    setRatio(DEFAULT_SUBTITLE_SETTINGS.fontRatio);
  };

  const setTextTrackColor = (c) => {
    setFontColor(c);
    setAutoColor(false);
  };

  const saveTextTrackSettings = useCallback(() => {
    player.userEvents$.next({
      action: USER_SUBTITLE_SETTINGS_SAVE,
      value: {
        fontFamily,
        color: autoColor ? null : color,
        background: enableBackground,
        fontRatio: ratio
      }
    });
  }, [fontFamily, autoColor, color, enableBackground, ratio]);

  const level1Text = {
    ...useStyles()(TEXT_LEVEL_1_FONT_SIZE_ONLY),
    fontFamily: FONT_FAMILY_BROWN
  };

  const footer = (
    <section
      ref={footerRef}
      id="subtitle-settings-footer"
      style={STYLES_SECTION_TRACK_SETTIONS_FOOTER}
    >
      <LabeledButton
        id="btn-subtitle-setting-save"
        label="Enregistrer"
        customStyle={{ background: COLOR_WHITE, color: COLOR_BLACK }}
        onClick={() => { saveTextTrackSettings(); onSave(); }}
      />
      <LabeledButton
        id="btn-subtitle-setting-reset"
        label="Réinitialiser"
        onClick={resetTextTrackSettings}
      />
    </section>
  );

  return (
    <div style={useStyles()(TRACK_SETTINGS_WRAPPER_STYLES)}>
      <section style={subtitleStyle} ref={headerRef}>
        <div style={mockTrackStyle}>
          Aperçu des sous-titres
        </div>
      </section>
      <section
        id="setting-content"
        style={SECTION_CONTENT_TRACK_SETTINGS_STYLES}
      >
        <Scrollable forceInnerScroll>
          <div style={STYLES_SUBTITLE_SETTINGS_SECTION_CONTENT}>
            <div style={level1Text}>
              Police
            </div>
            <div style={STYLES_WITH_SPACE}>
              {[
                { label: 'Standard', active: fontFamily === FONT_FAMILY_BROWN, set: FONT_FAMILY_BROWN },
                { label: 'Adaptée à la dyslexie', active: fontFamily === FONT_FAMILY_OPEN_DYSLEXIC, set: FONT_FAMILY_OPEN_DYSLEXIC }
              ].map(({ label, active, set }, i, l) => (
                <Selectable
                  radioButton
                  key={label}
                  label={label}
                  ariaLabel={label}
                  index={i + 1}
                  value={label}
                  totalCount={l.length}
                  active={active}
                  focusable={enableAccessOutline}
                  onSelect={() => setUseStandardFont(set)}
                  first
                />
              ))}
            </div>
            <Seperator style={{ margin: 0 }} />
            <div style={level1Text}>
              Couleur du texte
            </div>
            <div style={STYLES_WITH_SPACE}>
              {ALLOWED_SUBTITLE_COLORS.map((c, i) => (
                <SimpleTooltip
                  content={COLOR_LANG[c]}
                  position="top"
                  first={i === 0}
                  last={i === ALLOWED_SUBTITLE_COLORS.length - 1}
                >
                  <Selectable
                    radioButton
                    key={c}
                    label=""
                    ariaLabel={COLOR_LANG[c]}
                    index={i + 1}
                    value={c}
                    totalCount={ALLOWED_SUBTITLE_COLORS.length}
                    active={color === c}
                    focusable={enableAccessOutline}
                    onSelect={() => setTextTrackColor(c)}
                    first
                    bubble={false}
                    style={{
                      ...{
                        background: c,
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        marginLeft: i === 0 ? '4px' : '0px'
                      },
                      ...(!autoColor && c === color && {
                        outline: '2px solid #FFFFFF',
                        outlineOffset: '2px'
                      }),
                      border: c === COLOR_BLACK && c !== color ? '1px solid #5C5C5C' : 'auto'
                    }}
                  />
                </SimpleTooltip>
              ))}
            </div>
            <div>
              <ToggleButton
                val={autoColor}
                onToggle={() => setAutoColor(!autoColor)}
                label={`Couleur automatique (${autoColor ? 'activé' : 'désactivé'})`}
                ariaLabel={`${autoColor ? 'Désactiver' : 'Activer'} la couleur automatique`}
              />
            </div>
            <Seperator style={{ margin: 0 }} />
            <div style={level1Text}>
              Arrière plan
            </div>
            <div>
              <ToggleButton
                val={enableBackground}
                onToggle={() => setShowBackground(!enableBackground)}
                label={enableBackground ? 'Activé' : 'Désactivé'}
                ariaLabel={`${enableBackground ? 'Désactiver' : 'Activer'} arrière-plan`}
              />
            </div>
            <Seperator style={{ margin: 0 }} />
            <div style={level1Text}>
              Taille
            </div>
            <div style={STYLES_TRACK_SETTINGS_LETTER_RATIO}>
              {FONT_RATIOS.map(({ ratio: r, label }, i) => (
                <SimpleTooltip
                  content={label}
                  position="top"
                  first={i === 0}
                  last={i === FONT_RATIOS.length - 1}
                >
                  <Selectable
                    radioButton
                    key={r}
                    label="Aa"
                    ariaLabel={`Taille ${label}`}
                    index={i}
                    value={r}
                    totalCount={FONT_RATIOS.length}
                    active={ratio === r}
                    focusable={enableAccessOutline}
                    onSelect={() => setRatio(r)}
                    first
                    bubble={false}
                    styleLabel={{ margin: '0 10px', fontSize: fontSize * r }}
                    style={{
                      ...{
                        borderRadius: 8,
                        marginLeft: '0px',
                        alignItems: 'stretch',
                        display: 'flex',
                        justifyContent: 'center'
                      },
                      ...(r === ratio && {
                        outline: '2px solid #FFFFFF',
                        outlineOffset: '2px'
                      })
                    }}
                  />
                </SimpleTooltip>
              ))}
            </div>
            {extraSmall && footer}
          </div>
        </Scrollable>
      </section>
      {!extraSmall && footer}
    </div>
  );
}

export default withBreakPoints(connect(({ subtitles: { settings } }) => (settings))(TextTrackSettings));
